import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { MessageSquare, Wrench, Database, GitBranch, Play } from 'lucide-react';

// Nodo de Instrucción
export const InstructionNode = memo(({ data, selected }: any) => {
  return (
    <div className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-[200px] max-w-[300px] ${
      selected ? 'border-blue-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3" />

      <div className="flex items-center gap-2 mb-2">
        <MessageSquare size={16} className="text-blue-600" />
        <div className="font-semibold text-sm text-gray-900">Instrucción</div>
      </div>

      <div className="text-xs text-gray-600 break-words">
        {data.instruction || 'Haz clic para editar...'}
      </div>

      <Handle type="source" position={Position.Bottom} className="w-3 h-3" />
    </div>
  );
});

// Nodo de Tool
export const ToolNode = memo(({ data, selected }: any) => {
  return (
    <div className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-[200px] max-w-[300px] ${
      selected ? 'border-purple-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3" />

      <div className="flex items-center gap-2 mb-2">
        <Wrench size={16} className="text-purple-600" />
        <div className="font-semibold text-sm text-gray-900">Tool</div>
      </div>

      <div className="space-y-1">
        <div className="text-sm font-medium text-gray-900">{data.toolName}</div>
        {data.toolDescription && (
          <div className="text-xs text-gray-600 line-clamp-2">{data.toolDescription}</div>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} className="w-3 h-3" />
    </div>
  );
});

// Nodo de Resource
export const ResourceNode = memo(({ data, selected }: any) => {
  return (
    <div className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-[200px] max-w-[300px] ${
      selected ? 'border-green-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3" />

      <div className="flex items-center gap-2 mb-2">
        <Database size={16} className="text-green-600" />
        <div className="font-semibold text-sm text-gray-900">Recurso</div>
      </div>

      <div className="space-y-1">
        <div className="text-sm font-medium text-gray-900">{data.resourceName}</div>
        {data.resourceType && (
          <div className="text-xs text-gray-500 uppercase">{data.resourceType}</div>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} className="w-3 h-3" />
    </div>
  );
});

// Nodo de Condición
export const ConditionNode = memo(({ data, selected }: any) => {
  return (
    <div className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-[200px] max-w-[300px] ${
      selected ? 'border-yellow-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3" />

      <div className="flex items-center gap-2 mb-2">
        <GitBranch size={16} className="text-yellow-600" />
        <div className="font-semibold text-sm text-gray-900">Condición</div>
      </div>

      <div className="text-xs text-gray-600 break-words">
        {data.condition || 'Especifica condición...'}
      </div>

      <div className="flex justify-between mt-2">
        <Handle
          type="source"
          position={Position.Bottom}
          id="true"
          className="w-3 h-3 !left-[25%]"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="false"
          className="w-3 h-3 !left-[75%]"
        />
      </div>
    </div>
  );
});

// Nodo de Inicio
export const StartNode = memo(({ selected }: any) => {
  return (
    <div className={`px-6 py-3 rounded-full border-2 bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg ${
      selected ? 'border-white' : 'border-transparent'
    }`}>
      <div className="flex items-center gap-2">
        <Play size={20} className="text-white fill-white" />
        <div className="font-semibold text-white">Inicio</div>
      </div>

      <Handle type="source" position={Position.Bottom} className="w-3 h-3" />
    </div>
  );
});

// Exportar tipos de nodos
export const nodeTypes = {
  start: StartNode,
  instruction: InstructionNode,
  tool: ToolNode,
  resource: ResourceNode,
  condition: ConditionNode,
};
